import clsx from 'clsx';
import {
  ATTRIBUTION_SOURCE_LABELS,
  AttributionSourceBadge,
  isAttributionLinkSource,
} from 'common/constants/attribution-link-source';
import { Marketplaces } from 'common/constants/enums';
import { Button } from 'common/ui/button';
import { Pagination } from 'common/ui/pagination';
import { Table } from 'common/ui/table';
import { Tooltip } from 'common/ui/tooltip';
import { format, parseISO } from 'date-fns';
import { useAppSelector } from 'hooks';

import { BinIcon, CopyIcon } from '../../../../assets/images/icons';
import amazonLogoUrl from '../../../../assets/images/logos/amazon-icon.png';
import walmartLogoUrl from '../../../../assets/images/logos/walmart-icon.png';
import { utilCopyToClipboard } from '../../../../common/utils/utilCopyToClipboard';
import styles from './temporary-links-table.module.scss';

const MARKETPLACE_LOGOS: Record<string, string> = {
  [Marketplaces.Amazon]: amazonLogoUrl,
  [Marketplaces.Walmart]: walmartLogoUrl,
};

const TABLE_COLUMNS = ['Link', 'Source', 'Created', 'Expired', 'Actions'];

interface TemporaryLinksTableProps {
  count: number;
  limit: number;
  onLimitChange(limit: number): void;
  onOffsetChange(offset: number): void;
  handleDeleteLink(link: string): Promise<void>;
}

const TemporaryLinksTable = ({
  count,
  handleDeleteLink,
  limit,
  onLimitChange,
  onOffsetChange,
}: TemporaryLinksTableProps) => {
  const links = useAppSelector((state) => state.temporaryLinks.tempLinks);
  const status = useAppSelector((state) => state.temporaryLinks.status);

  if (status === 'succeeded' && links.length === 0) {
    return (
      <Table tableLayout="auto">
        <Table.Body>
          <Table.NoResult visible />
          <div style={{ marginBottom: '20px' }} />
        </Table.Body>
      </Table>
    );
  }

  const tableColumnLayout = (
    <colgroup>
      <col className={styles.colLink} />
      <col className={styles.colSource} />
      <col className={styles.colDate} />
      <col className={styles.colDate} />
      <col className={styles.colActions} />
    </colgroup>
  );

  return (
    <Table className={styles.tableWrapper} tableLayout="fixed">
      {tableColumnLayout}
      {(() => {
        if (status === 'pending') {
          return (
            <Table.Body>
              <div style={{ margin: '50px 0' }}>
                <Table.Loader loading size="medium">
                  Loading
                </Table.Loader>
              </div>
            </Table.Body>
          );
        }

        if (status === 'succeeded' && links.length === 0) {
          return (
            <Table.Body>
              <Table.NoResult visible />
              <div style={{ marginBottom: '20px' }} />
            </Table.Body>
          );
        }

        if (status === 'succeeded' && links.length > 0) {
          return (
            <>
              <Table.Header>
                <Table.Row>
                  {TABLE_COLUMNS.map((column) => (
                    <Table.Column
                      key={column}
                      className={clsx(
                        styles.tableHeader,
                        column === 'Link' && styles.linkColumn,
                        column === 'Source' && styles.sourceColumn,
                        (column === 'Created' || column === 'Expired') && styles.dateColumn,
                        column === 'Actions' && styles.actionColumn,
                      )}
                    >
                      {column}
                    </Table.Column>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {links.map((link) => (
                  <Table.Row key={link.link}>
                    <Table.Column className={styles.linkColumn} style={{ fontWeight: 400 }}>
                      <div className={styles.linkCell}>
                        <span className={styles.linkContent}>
                          <span className={styles.linkContentInner}>
                            {link.product_marketplace && (
                              <img
                                alt={link.product_marketplace}
                                className={styles.marketplaceIcon}
                                src={MARKETPLACE_LOGOS[link.product_marketplace] ?? amazonLogoUrl}
                              />
                            )}
                            <span className={styles.linkText}>{link.link}</span>
                          </span>
                        </span>
                        <Button
                          className={styles.copyButton}
                          iconStart={<CopyIcon />}
                          onClick={(event) => {
                            event.stopPropagation();
                            utilCopyToClipboard(link.link);
                          }}
                          outlined
                          size="small"
                          type="button"
                          variant="secondary"
                        />
                      </div>
                    </Table.Column>
                    <Table.Column className={styles.sourceColumn}>
                      {isAttributionLinkSource(link.source) ? (
                        <Tooltip
                          className={styles.sourceIconWrapper}
                          title={ATTRIBUTION_SOURCE_LABELS[link.source]}
                        >
                          <AttributionSourceBadge
                            iconClassName={styles.sourceIcon}
                            iconOnly
                            source={link.source}
                          />
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </Table.Column>
                    <Table.Column className={styles.dateColumn}>
                      {link?.created_at ? format(parseISO(link.created_at), 'dd MMM yyyy') : '-'}
                    </Table.Column>
                    <Table.Column className={styles.dateColumn}>
                      {link?.expired_at
                        ? format(parseISO(link.expired_at), 'dd MMM yyyy') : '-'}
                    </Table.Column>
                    <Table.Column className={styles.actionColumn}>
                      <span className={styles.actionCellContent}>
                        <Button
                          iconStart={<BinIcon />}
                          onClick={() => handleDeleteLink(link.link)}
                          outlined
                          placeholder="tags"
                          size="small"
                          variant="danger"
                        />
                      </span>
                    </Table.Column>
                  </Table.Row>
                ))}
              </Table.Body>
            </>
          );
        }

        return null;
      })()}
      <Table.Footer>
        <Pagination
          count={count}
          limit={limit}
          limitHandler={onLimitChange}
          onChange={(offset) => onOffsetChange(offset)}
        />
      </Table.Footer>
    </Table>
  );
};

export { TemporaryLinksTable };
